import { action, observable, makeObservable } from "mobx";
import {AppConfiguration} from "../core/config/app-configuration";
import {ThematicInspectorConfiguration} from "../config/thematic-inspector-configuration";
import _ from "lodash";

import {
    Grundstueck
} from "../models/grundstueck";
import {Projekt} from "../models/projekt";
import {Gebaeude} from "../models/gebaeude";
import {MapState} from "../core/map-state";
import {Objektlu} from "../models/objektlu";

export class ObjektluStore {
    loading: boolean;
    status: "loading" | "successful" | "error" | "not-found";
    // remember the selected objekt
    selectedObjektId: string;
    // remember the selected objekt type
    selectedObjektType: string;
    // remember grundstueck by egrid
    grundstuecke = observable.map<string, Grundstueck>();
    // remember gebaeude by egid
    gebaeude = observable.map<number, Gebaeude>();
    // remember projekt by bagenr
    projekte = observable.map<string, Projekt>();
    edid: number | undefined;
    zonenplanLoaded = false;
    projektpreviewLoaded = false;
    showMap = true;

    zonenplanLoading = false;
    projektpreviewLoading = false;
    // lookup table gbperCode-nummer to egrid
    grundstueckMap: Map<string, string> = new Map();
    zonenplanMap: Map<number, string> = new Map();
    zonenplanSymbolMap: Map<number, __esri.Symbol> = new Map();
    zonenplanHelperArray: number[];
    projektpreviewMap: Map<string, string> = new Map();
    config: AppConfiguration;
    tiConfig: ThematicInspectorConfiguration;
    mapStore: MapState;
    statusText = "";
    objektlu: Objektlu;
    constructor(config: AppConfiguration, tiConfig: ThematicInspectorConfiguration, mapStore: MapState,
                layerlist: HashMap<HashMap<string|number>>) {
        makeObservable(this, {
            loading: observable,
            status: observable,
            selectedObjektId: observable,
            selectedObjektType: observable,
            grundstuecke: observable,
            gebaeude: observable,
            projekte: observable,
            edid: observable,
            zonenplanLoaded: observable,
            projektpreviewLoaded: observable,
            showMap: observable,

            addGrundstueck: action,
            addGebaeude: action,
            addProjekt: action,
            setEdid: action,
            setShowMap: action,
            setSelect: action,
            setProjektpreviewLoading: action,
            setZonenplanLoading: action,
            resetSelect: action
        });
        this.config = config;
        this.tiConfig = tiConfig;
        this.mapStore = mapStore;
        this.objektlu = new Objektlu(
            config,
            tiConfig,
            this.mapStore,
            layerlist, this);
    }

    addGrundstueck(grundstueck: Grundstueck): void {
        if (this.grundstuecke.has(grundstueck.egrid)) {
            this.grundstuecke.delete(grundstueck.egrid);
        }
        this.grundstuecke.set(grundstueck.egrid, grundstueck);
        this.grundstueckMap.set(grundstueck.gbperCode + "-" + grundstueck.nummer, grundstueck.egrid);
    }

    addGebaeude(gebaeude: Gebaeude): void {
        if (this.gebaeude.has(gebaeude.egid)) {
            this.gebaeude.delete(gebaeude.egid);
        }
        this.gebaeude.set(gebaeude.egid, gebaeude);
    }
    addProjekt(projekt: Projekt): void {
        if (this.projekte.has(projekt.bagenr)) {
            this.projekte.delete(projekt.bagenr);
        }
        this.projekte.set(projekt.bagenr, projekt);
    }
    setEdid(edid: number | undefined): void {
        this.edid = edid;
    }
    setShowMap(show: boolean): void {
        this.showMap = show;
    }
    setSelect(objectId: string, objectType: string, edid?: number | string | undefined): void {
        if (typeof edid === "string"){
            edid = parseInt(edid, 10);
        }
        this.selectedObjektId = objectId;
        this.selectedObjektType = objectType;
        this.edid = edid;
    }
    setProjektpreviewLoading(flag: boolean, loadedFlag?: boolean): void {
        this.projektpreviewLoading = flag;
        if (typeof loadedFlag !== "undefined") {
            this.projektpreviewLoaded = loadedFlag;
        }
    }
    setZonenplanLoading(flag: boolean, loadedFlag?: boolean): void {
        this.zonenplanLoading = flag;
        if (typeof loadedFlag !== "undefined") {
            this.zonenplanLoaded = loadedFlag;
        }
    }
    resetSelect(): void {
        this.selectedObjektId = "";
        this.selectedObjektType = "";
        this.edid = undefined;
    }
    getGrundstueck(egrid: string): Grundstueck | null  {
        const grundstueck = this.grundstuecke.get(egrid);
        return grundstueck || null;
    }
    selectedGrundstueck(): Grundstueck | null {
        const objektId = this.selectedObjektId;
        if (!objektId) {
            return null;
        }
        const grundstueck = this.grundstuecke.get(objektId);
        return grundstueck || null;
    }
    selectedGebaeude(): Gebaeude | null {
        const objektId = this.selectedObjektId;
        if (!objektId) {
            return null;
        }
        const gebaeude = this.gebaeude.get(parseInt(objektId, 10));
        return gebaeude || null;
    }
    selectedProjekt(): Projekt | null {
        const objektId = this.selectedObjektId;
        if (!objektId) {
            return null;
        }
        const projekt = this.projekte.get(objektId);
        return projekt || null;
    }
    requestGrundstueck(egrid: string): void {
        const grundstueck = this.grundstuecke.get(egrid);
        if (!grundstueck) {
            this.objektlu.loadGrundstueck(egrid).then((returnEgrid) => {
                let selectedObjektId: string;
                if (typeof returnEgrid === "string") {
                    selectedObjektId = returnEgrid;
                } else {
                    if (this.status === "not-found") {
                        selectedObjektId = "not-found";
                    } else {
                        selectedObjektId = "error";
                    }
                }
                this.setSelect(selectedObjektId, "grundstueck");
            });
        } else {
            this.setSelect(grundstueck.egrid, "grundstueck");
        }
    }

    requestGebaeude(egid: string | number, edid: string | number | undefined): void {
        if (typeof egid === "string"){
            egid = parseInt(egid, 10);
        }
        const gebaeude = this.gebaeude.get(egid);
        if (!gebaeude) {
            this.objektlu.loadGebaeude(egid).then((returnEgid) => {
                let selectedObjektId: string;
                if (typeof returnEgid === "number") {
                    selectedObjektId = returnEgid.toString();
                } else {
                    if (this.status === "not-found") {
                        selectedObjektId = "not-found";
                    } else {
                        selectedObjektId = "error";
                    }
                    edid = undefined;
                }
                this.setSelect(selectedObjektId, "gebaeude", edid);
            });
        } else {
            this.setSelect(gebaeude.egid.toString(), "gebaeude", edid);
        }
    }
    requestProjekt(bagenr: string): void {
        const projekt = this.projekte.get(bagenr);
        if (!projekt) {
            this.objektlu.loadProjekt(bagenr).then((returnBagenr) => {
                let selectedObjektId: string;
                if (typeof returnBagenr === "string") {
                    selectedObjektId = returnBagenr;
                } else {
                    if (this.status === "not-found") {
                        selectedObjektId = "not-found";
                    } else {
                        selectedObjektId = "error";
                    }
                }
                this.setSelect(selectedObjektId, "projekt");
            });
        } else {
            this.setSelect(projekt.bagenr, "projekt");
        }
    }
    getZonenplanPreview(value: number): string | void {
        if (!this.zonenplanLoaded && !this.zonenplanLoading) {
            this.objektlu.loadZonenplan();
        }
        const mapIndex = _.find(this.zonenplanHelperArray, (n: number) => n<=value);
        if (!mapIndex){
            return;
        } else {
            return this.zonenplanMap.get(mapIndex);
        }
    }
    getZonenplanSymbol(value: number): __esri.Symbol | undefined {
        if (!this.zonenplanLoaded && !this.zonenplanLoading) {
            this.objektlu.loadZonenplan();
        }
        const mapIndex = _.find(this.zonenplanHelperArray, (n: number) => n<=value);
        if (!mapIndex){
            return undefined;
        } else {
            return this.zonenplanSymbolMap.get(mapIndex);
        }
    }
    loadZonenplan(): Promise<void>  {
        return this.objektlu.loadZonenplan();
    }
    getProjektPreview(value: string): string | undefined {
        if (!this.projektpreviewLoaded && !this.projektpreviewLoading) {
            this.objektlu.loadProjektPreview();
        }
        return this.projektpreviewMap.get(value);
    }
    loadProjektPreview(): void {
        this.objektlu.loadProjektPreview();
    }
}
